import { CollectibleId_Schema } from "@/services/collectibles";
import { CollectibleElastic_Schema } from "@/services/collectibles/elastic";
import { PublicCollectionItem_Basics_Schema } from "@/services/collection/item/public/publicCollectionItemSchemaPartials";
import { ShippingMethodId_Schema } from "@/services/marketplace/shippingMethod";
import { PriceNumeric_Schema } from "@/services/prices";
import { UserPublicInfo_Schema } from "@/services/user";
import { DateTimeIso8601_Schema, DateTimeIso8601Local_Schema, DateTimeObject_Schema, NaturalNumber_Schema, WholeNumber_Schema } from "@/utilities";
import { z } from "zod";
import { PriceElastic_Schema } from "@/services/prices/priceSchema";

export const MarketplaceItemId_Schema = WholeNumber_Schema;

export const MarketplaceItem_Schema = z
  .object({
    collectible: z
      .object({
        id: CollectibleId_Schema,
        name: z.string(),
      })
      .nullable(),
    collectionItem: PublicCollectionItem_Basics_Schema,
    id: MarketplaceItemId_Schema,
    delivery: z.object({
      id: z.number().int().positive(),
      delivery: ShippingMethodId_Schema,
      price: PriceNumeric_Schema,
      numberPerDelivery: z.number().int().min(1),
    }),
    itemPrice: z.object({
      id: z.number().int().positive(),
      price: PriceNumeric_Schema,
    }),
    expiresAt: DateTimeIso8601Local_Schema,
    name: z.string(),
    note: z.string(),
    quantity: z.number().int().min(0),
    createdDateTime: DateTimeObject_Schema,
    user: UserPublicInfo_Schema,
    collectibleElastic: CollectibleElastic_Schema.nullable(),
  })
  .meta({ openapi: "MarketPlaceItemReadResponse" })
  .transform((response) => ({
    id: response.id,
    name: response.name,
    collectible: response.collectible,
    collectibleElastic: response.collectibleElastic,
    collectionItem: response.collectionItem,
    categoryId: response.collectionItem.categoryId,
    price: response.itemPrice.price,
    priceId: response.itemPrice.id,
    priceBeforeConversion: response.itemPrice.price, // @todo should be sent from API
    description: response.note,
    images: response.collectionItem.images,
    createdDateTime: response.createdDateTime,
    expirationDateTime: response.expiresAt,
    possibleShippings: [
      {
        id: response.delivery.id,
        shippingMethodId: response.delivery.delivery,
        price: response.delivery.price,
        quantityPerPackage: response.delivery.numberPerDelivery,
      },
    ],
    quantity: response.quantity,
    owner: response.user,
  }));

export const MarketplaceItemPreview_Schema = z.object({
  id: MarketplaceItemId_Schema,
  name: z.string(),
  price: PriceNumeric_Schema,
  quantity: WholeNumber_Schema,
});

export const AvailableMarketplaceItemShipping_Schema = z
  .object({
    id: NaturalNumber_Schema,
    delivery: ShippingMethodId_Schema,
    price: PriceNumeric_Schema,
    numberPerDelivery: NaturalNumber_Schema,
  })
  .transform(({ id, delivery, price, numberPerDelivery }) => ({
    id,
    shippingMethodId: delivery,
    price,
    quantityPerPackage: numberPerDelivery,
  }));

export const MarketplaceItemElastic_Schema = z
  .object({
    id: MarketplaceItemId_Schema,
    note: z.string(),
    createdAt: DateTimeIso8601_Schema,
    expiresAt: DateTimeIso8601_Schema,
    // Delivery should never be null in practice, but if any step of the marketplace item
    // creation fails, it may be temporarily null until the issue is resolved.
    delivery: AvailableMarketplaceItemShipping_Schema.nullable(),
    quantity: WholeNumber_Schema,
    priceBeforeConversion: PriceElastic_Schema.array(),
    prices: PriceElastic_Schema.array(),
  })
  .transform(({ prices, priceBeforeConversion: pricesBeforeConversion, createdAt, note, delivery, expiresAt, ...rest }) => {
    // Use only the first one price. Only single one price is sent from API anyway.
    // Also we dont want it in cents.
    let price = undefined;
    if (prices.length > 0) {
      price = {
        price: prices[0].price / 100,
        currencyId: prices[0].currencyId,
      };
    }

    // Use only the first one price. Only single one price is sent from API anyway.
    // Also we dont want it in cents.
    let priceBeforeConversion = undefined;
    if (pricesBeforeConversion.length > 0) {
      priceBeforeConversion = {
        price: pricesBeforeConversion[0].price / 100,
        currencyId: pricesBeforeConversion[0].currencyId,
      };
    }

    return {
      ...rest,
      price,
      priceBeforeConversion,
      /** @deprecated Use availableShippings instead. */
      possibleShippings: delivery === null ? [] : [delivery],
      availableShippings: delivery === null ? [] : [delivery],
      dateTimeCreated: createdAt,
      dateTimeExpiration: expiresAt,
      description: note,
    };
  });
